import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { apiRequest } from '@/config';

type ProviderKey = 'openai' | 'anthropic' | 'fireworks';

const MODELS: Record<
  ProviderKey,
  { id: string; name: string; defaultTokens: number }[]
> = {
  openai: [
    { id: 'gpt-5-2025-08-07', name: 'GPT-5', defaultTokens: 128000 },
    { id: 'gpt-5.1-2025-11-13', name: 'GPT-5.1', defaultTokens: 128000 },
    { id: 'gpt-5-nano-2025-08-07', name: 'GPT-5 Nano', defaultTokens: 128000 },
    { id: 'gpt-4o-2024-08-06', name: 'GPT-4o', defaultTokens: 16384 },
    { id: 'gpt-5.2-2025-12-11', name: 'GPT-5.2', defaultTokens: 128000 },
  ],
  anthropic: [
    {
      id: 'claude-sonnet-4-5-20250929',
      name: 'Claude Sonnet 4.5',
      defaultTokens: 32000,
    },
    {
      id: 'claude-sonnet-4-20250514',
      name: 'Claude Sonnet 4',
      defaultTokens: 32000,
    },
    {
      id: 'claude-opus-4-1-20250805',
      name: 'Claude Opus 4',
      defaultTokens: 32000,
    },
  ],
  fireworks: [
    {
      id: 'accounts/fireworks/models/llama4-maverick-instruct-basic',
      name: 'Llama 4 Maverick',
      defaultTokens: 16000,
    },
    {
      id: 'accounts/fireworks/models/qwen3-30b-a3b',
      name: 'Qwen3 30B',
      defaultTokens: 16000,
    },
    {
      id: 'accounts/fireworks/models/kimi-k2-instruct-0905',
      name: 'Kimi K2 Instruct',
      defaultTokens: 16000,
    },
  ],
};

export function CreateConfigModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [provider, setProvider] = useState<ProviderKey>('openai');
  const [apiModel, setApiModel] = useState(MODELS.openai[0].id);
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(128000);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const availableModels = MODELS[provider];

  function handleProviderChange(newProvider: ProviderKey) {
    setProvider(newProvider);
    const firstModel = MODELS[newProvider][0];
    setApiModel(firstModel.id);
    setMaxTokens(firstModel.defaultTokens);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Derive model_key from name (lowercase, hyphenated)
      const modelKey = name.toLowerCase().replace(/\s+/g, '-');

      await apiRequest('/api/eval/configs', {
        method: 'POST',
        body: JSON.stringify({
          name,
          model_key: modelKey,
          provider,
          api_model: apiModel,
          temperature,
          max_tokens: maxTokens,
          system_prompt: systemPrompt.trim() || undefined,
        }),
      });

      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Create Model Configuration"
      onClose={onClose}
      maxWidth="600px"
    >
      <form onSubmit={handleSubmit}>
        {/* Name */}
        <div className="mb-5">
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Configuration Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            className="w-full px-3 py-3 rounded-md text-base"
            style={{ border: '1px solid #E8E0D8' }}
            placeholder="e.g., GPT-5 High Temperature"
          />
        </div>

        {/* Provider */}
        <div className="mb-5">
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Provider *
          </label>
          <div className="flex gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => handleProviderChange('openai')}
              className="flex-1 min-w-[100px] py-3 rounded-md font-medium cursor-pointer transition-colors"
              style={{
                border: `2px solid ${provider === 'openai' ? '#8B2332' : '#E8E0D8'}`,
                background:
                  provider === 'openai' ? 'rgba(139, 35, 50, 0.08)' : 'white',
                color: provider === 'openai' ? '#8B2332' : '#1A1A1A',
              }}
            >
              OpenAI
            </button>
            <button
              type="button"
              onClick={() => handleProviderChange('anthropic')}
              className="flex-1 min-w-[100px] py-3 rounded-md font-medium cursor-pointer transition-colors"
              style={{
                border: `2px solid ${provider === 'anthropic' ? '#8B2332' : '#E8E0D8'}`,
                background:
                  provider === 'anthropic'
                    ? 'rgba(139, 35, 50, 0.08)'
                    : 'white',
                color: provider === 'anthropic' ? '#8B2332' : '#1A1A1A',
              }}
            >
              Anthropic
            </button>
            <button
              type="button"
              onClick={() => handleProviderChange('fireworks')}
              className="flex-1 min-w-[100px] py-3 rounded-md font-medium cursor-pointer transition-colors"
              style={{
                border: `2px solid ${provider === 'fireworks' ? '#8B2332' : '#E8E0D8'}`,
                background:
                  provider === 'fireworks'
                    ? 'rgba(139, 35, 50, 0.08)'
                    : 'white',
                color: provider === 'fireworks' ? '#8B2332' : '#1A1A1A',
              }}
            >
              Fireworks
            </button>
          </div>
        </div>

        {/* Model */}
        <div className="mb-5">
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Model *
          </label>
          <select
            value={apiModel}
            onChange={e => setApiModel(e.target.value)}
            className="w-full px-3 py-3 rounded-md text-base bg-white"
            style={{ border: '1px solid #E8E0D8' }}
          >
            {availableModels.map(model => (
              <option key={model.id} value={model.id}>
                {model.name} ({model.id})
              </option>
            ))}
          </select>
        </div>

        {/* Temperature */}
        <div className="mb-5">
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Temperature: {temperature}
          </label>
          <input
            type="range"
            min="0"
            max="2"
            step="0.1"
            value={temperature}
            onChange={e => setTemperature(parseFloat(e.target.value))}
            className="w-full"
            style={{ accentColor: '#8B2332' }}
          />
          <div
            className="flex justify-between text-xs mt-1"
            style={{ color: '#A0A0A0' }}
          >
            <span>0 (Deterministic)</span>
            <span>2 (Creative)</span>
          </div>
        </div>

        {/* Max Tokens */}
        <div className="mb-5">
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Max Tokens
          </label>
          <input
            type="number"
            value={maxTokens}
            onChange={e => setMaxTokens(parseInt(e.target.value, 10))}
            className="w-full px-3 py-3 rounded-md text-base"
            style={{ border: '1px solid #E8E0D8' }}
          />
        </div>

        {/* System Prompt */}
        <div className="mb-6">
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Custom System Prompt (optional)
          </label>
          <textarea
            value={systemPrompt}
            onChange={e => setSystemPrompt(e.target.value)}
            rows={4}
            className="w-full px-3 py-3 rounded-md text-base font-inherit resize-y"
            style={{ border: '1px solid #E8E0D8' }}
            placeholder="Leave empty to use default system prompt"
          />
        </div>

        {error && (
          <div
            className="mb-4 p-3 rounded-md text-sm"
            style={{
              background: 'rgba(196, 30, 58, 0.08)',
              border: '1px solid #C41E3A',
              color: '#C41E3A',
            }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || !name.trim()}
          >
            {submitting ? 'Creating...' : 'Create Config'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
